// ./src/utils/api/TicketPriceRuleService.ts
//
// Variable-pricing rules on a ticket type (early-bird / time-of-week / membership
// / volume / availability). Mirrors the eventsmagic-api TicketPriceRule SDL.
// `where.id` accepts one id or many — a bulk update (>1 id) may only change status
// (server-enforced). Rules are evaluated by orderNo; is_stackable decides whether a
// rule layers on top of others or is exclusive (first match wins).
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

/** What condition a rule keys off (mirrors boundary TICKET_PRICE_RULE_KIND). */
export const TICKET_PRICE_RULE_KIND = {
  DATE_WINDOW: 'date_window',
  VOLUME: 'volume',
  AVAILABILITY: 'availability',
  TIME_OF_WEEK: 'time_of_week',
  MEMBERSHIP: 'membership',
} as const;

/** How a matching rule changes the price (mirrors boundary TICKET_PRICE_EFFECT_KIND). */
export const TICKET_PRICE_EFFECT_KIND = {
  SET_PRICE: 'set_price',
  ADJUST_AMOUNT: 'adjust_amount',
  ADJUST_PERCENT: 'adjust_percent',
} as const;

const FIELDS = `
  id
  ticketTypeId
  name
  ruleKind
  effectKind
  amount
  isStackable
  orderNo
  startsAt
  endsAt
  minQuantity
  maxQuantity
  minSold
  maxSold
  daysOfWeek
  timeFrom
  timeTo
  membershipTypeId
  conditions
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query TicketPriceRuleList($filter: TicketPriceRuleFilter, $params: PaginationAndSorting) {
    ticketPriceRuleList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation TicketPriceRuleCreate($params: TicketPriceRuleInput) {
    ticketPriceRuleCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation TicketPriceRuleUpdate($where: TicketPriceRuleWhereInput, $params: TicketPriceRuleInput) {
    ticketPriceRuleUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SET_TIERS_MUTATION = gql`
  mutation TicketPriceTiersSet($params: TicketPriceTiersSetInput) {
    ticketPriceTiersSet(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation TicketPriceRuleRemove($where: TicketPriceRuleWhereInput) {
    ticketPriceRuleRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface TicketPriceRule {
  id?: string;
  ticketTypeId?: string | null;
  name?: string | null;
  ruleKind?: string | null;
  effectKind?: string | null;
  amount?: number | null;
  isStackable?: boolean | null;
  orderNo?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  minSold?: number | null;
  maxSold?: number | null;
  daysOfWeek?: number[] | null;
  timeFrom?: string | null;
  timeTo?: string | null;
  membershipTypeId?: string | null;
  conditions?: Record<string, unknown> | null;
  status?: number | null;
  rights?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface TicketPriceRuleInput {
  ticketTypeId?: string | null;
  name?: string | null;
  ruleKind?: string | null;
  effectKind?: string | null;
  amount?: number | null;
  isStackable?: boolean | null;
  orderNo?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  minSold?: number | null;
  maxSold?: number | null;
  daysOfWeek?: number[] | null;
  timeFrom?: string | null;
  timeTo?: string | null;
  membershipTypeId?: string | null;
  conditions?: Record<string, unknown> | null;
  status?: number | null;
}

export class TicketPriceRuleService {
  static async list(ticketTypeId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { ticketTypeId: [ticketTypeId], status: statuses },
    });
  }

  static async create(params: TicketPriceRuleInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: TicketPriceRuleInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /**
   * #162 — replace a ticket type's price tiers ("the next N tickets at $X", in order) in one transaction; an empty list
   * removes them. Returns the type's rules as they now stand. Refusals name the row: `tiers[<i>].quantity` / `tiers[<i>].price` (`getIndexedFormErrors`).
   */
  static async setTiers(ticketTypeId: string, tiers: Array<{ quantity: number; price: number }>): Promise<OpResult> {
    return ApiService.executeMutation(SET_TIERS_MUTATION, { params: { ticketTypeId, tiers } });
  }
}
